/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { SendOrderEmailRequest } from './Requests/SendOrderEmailRequest';
import { SendSignupEmailRequest } from './Requests/SendSignupEmailRequest';
import { CreateBiddingRoomUserRequest } from './Requests/CreateBiddingRoomUserRequest';
import { CreateCustomerRequest } from './Requests/CreateCustomerRequest';
import { PurchaseMaterialBidRequest } from './Requests/PurchaseMaterialBidRequest';
import { PurchasePremiumSubscriptionRequest } from './Requests/PurchasePremiumSubscriptionRequest';
import { CancelPremiumSubscriptionRequest } from './Requests/CancelPremiumSubscriptionRequest';
import { UpdateBrOrderStatusByOrderIdRequest } from './Requests/UpdateBrOrderStatusByOrderIdRequest';
import { updateCurrentUser, updateFirebaseAuthToken } from '../../redux/features/current_user/currentUserSlice';
import { User_BrUser } from '../../data/models/User/User_BrUser';
import { CreatePaymentMethodInStripeWithIxoTokenRequest } from './Requests/CreatePaymentMethodInStripeWithIxoTokenRequest';
import { GetUpdateCVCIframeConfigRequest } from './Requests/GetUpdateCVCIframeConfigRequest';
import { getAuth } from 'firebase/auth';
import { jwtDecode } from 'jwt-decode';
import { DetachDefaultPaymentMethodFromCustomerRequest } from './Requests/DetachDefaultPaymentMethodFromCustomerRequest';
import { useAppDispatch, useAppSelector } from '../../redux/hooks/hooks';

const SEARCH_SERP_API_HOME_DEPOT = import.meta.env.VITE_SEARCH_SERP_API_HOME_DEPOT || 'https://mock-api.com/serpApiHomeDepotSearchQuery'
const GET_PRODUCT_SERP_API_HOME_DEPOT = import.meta.env.VITE_GET_PRODUCT_SERP_API_HOME_DEPOT || 'https://mock-api.com/serpApiHomeDepotGetProductQuery'
const SEND_ORDER_EMAIL = import.meta.env.VITE_SEND_ORDER_EMAIL || 'https://mock-api.com/sendOrderEmail'
const SEND_SIGNUP_EMAIL = import.meta.env.VITE_SEND_SIGNUP_EMAIL || 'https://mock-api.com/sendSignupEmail'
const CREATE_CUSTOMER = import.meta.env.VITE_CREATE_CUSTOMER || 'https://mock-api.com/createCustomer'
const VALIDATE_BETA_KEY = import.meta.env.VITE_VALIDATE_BETA_KEY || 'https://mock-api.com/validateBetaKey'
const VALIDATE_ORDER_ADMIN_KEY = import.meta.env.VITE_VALIDATE_ORDER_ADMIN_KEY || 'https://mock-api.com/validateOrderAdminKey'
const CREATE_BIDDING_ROOM_USER = import.meta.env.VITE_CREATE_BIDDING_ROOM_USER || 'https://mock-api.com/createBiddingRoomUser'
const GET_BIDDING_ROOM_USER_BY_EMAIL = import.meta.env.VITE_GET_BIDDING_ROOM_USER_BY_EMAIL || 'https://mock-api.com/getBiddingRoomUserByEmail'
const PURCHASE_MATERIAL_BID = import.meta.env.VITE_PURCHASE_MATERIAL_BID || 'https://mock-api.com/purchaseMaterialBid'
const GET_CUSTOMER = import.meta.env.VITE_GET_CUSTOMER || 'https://mock-api.com/getCustomer'
const CANCEL_PREMIUM_SUBSCRIPTION = import.meta.env.VITE_CANCEL_PREMIUM_SUBSCRIPTION || 'https://mock-api.com/cancelPremiumSubscription'
const GET_ALL_BR_ORDERS = import.meta.env.VITE_GET_ALL_BR_ORDERS || 'https://mock-api.com/getAllBrOrders'
const UPDATE_BR_ORDER_STATUS_BY_ORDER_ID = import.meta.env.VITE_UPDATE_BR_ORDER_STATUS_BY_ORDER_ID || 'https://mock-api.com/updateBrOrderStatusByOrderId'
const GET_BR_ORDERS_BY_USER_ID = import.meta.env.VITE_GET_BR_ORDERS_BY_USER_ID || 'https://mock-api.com/getBrOrdersByUserId'

export const baseQueryWithAuth = async (args: any, api: any, extraOptions: any) => {
    const baseQuery = fetchBaseQuery({
        baseUrl: '',
        prepareHeaders: async (headers, { getState }) => {
            // Must import Redux store after FirebaseFunctionsApi initalizes
            const { store } = await import('../../redux/store/store');

            let token = (getState() as any).currentUser?.currentUser?.firebaseAuthToken;

            if (token) {
                const decodedToken: { exp: number } = jwtDecode(token);
                const isExpired = decodedToken.exp * 1000 < Date.now();

                if (isExpired) {
                    const auth = getAuth();
                    const user = auth.currentUser;

                    if (user) {
                        token = await user.getIdToken(true);
                        store.dispatch(updateFirebaseAuthToken(token));
                    }
                }

                headers.set('Authorization', `Bearer ${token}`);
            }

            return headers;
        },
    });

    const result = await baseQuery(args, api, extraOptions);

    // Check for unauthorized response
    if (result.error?.status === 401 || result.error?.status === 403) {
        const { store } = await import('../../redux/store/store');
        store.dispatch(updateCurrentUser(null)); // Clear user state

        // Redirect to login page
        window.location.href = '/login';
    }

    return result;
};

export const firebaseFunctionsApi = createApi({
    reducerPath: 'firebaseFunctionsApi',
    baseQuery: baseQueryWithAuth,
    endpoints: (builder) => ({
        serpApiHomeDepotSearch: builder.query({
            query: (searchQuery : string) => ({
                url: SEARCH_SERP_API_HOME_DEPOT,
                refetchOnMountArgChange: true,
                body: {
                    data: {
                        searchQuery: searchQuery
                    }
                },
                method : 'POST'
            }),
        }),
        serpApiHomeDepotGetProduct: builder.query({
            query: (productId : string) => ({
                url: GET_PRODUCT_SERP_API_HOME_DEPOT,
                refetchOnMountArgChange: true,
                body: {
                    data: {
                        productId: productId
                    }
                },
                method : 'POST'
            }),
        }),
        sendSignupEmail: builder.query({
            query: (request: SendSignupEmailRequest) => ({
                url: SEND_SIGNUP_EMAIL,
                body: {
                    data: {
                        fullName:        request.fullName,
                        businessName:    request.businessName,
                        email:           request.email,
                        phone:           request.phone,
                        businessAddress: request.businessAddress,
                        hearAboutUs:     request.hearAboutUs,
                    }
                },
                method: 'POST',
            }),
        }),
        sendOrderEmail: builder.query({
            query: (sendOrderEmailRequest : SendOrderEmailRequest) => ({
                url: SEND_ORDER_EMAIL,
                refetchOnMountArgChange: true,
                body: {
                    data: sendOrderEmailRequest
                },
                method : 'POST'
            }),
        }),
        createCustomer: builder.query({
            query: (request : CreateCustomerRequest) => ({
                url: CREATE_CUSTOMER,
                refetchOnMountArgChange: true,
                body: {
                    data: {
                        fullName : request.fullName,
                        email : request.email
                    }
                },
                method : 'POST'
            }),
        }),
        validateBetaKey: builder.query({
            query: (enteredKey : string) => ({
                url: VALIDATE_BETA_KEY,
                refetchOnMountArgChange: true,
                body: {
                    data: {
                        enteredKey : enteredKey
                    }
                },
                method : 'POST'
            }),
        }),        
        validateOrderAdminKey: builder.query({
            query: (enteredKey : string) => ({
                url: VALIDATE_ORDER_ADMIN_KEY,
                refetchOnMountArgChange: true,
                body: {
                    data: {
                        enteredKey : enteredKey
                    }
                },
                method : 'POST'
            }),
        }),
        createBiddingRoomUser: builder.query({
            query: (request : CreateBiddingRoomUserRequest) => ({
                url: CREATE_BIDDING_ROOM_USER,
                refetchOnMountArgChange: true,
                body: {
                    data: {
                        firstName : request.firstName,
                        lastName : request.lastName,
                        email : request.email,
                        phoneNumber : request.phoneNumber,
                        companyName : request.companyName,
                        stripeCustomerId : request.stripeCustomerId
                    }
                },
                method : 'POST'
            }),
        }),
        getBiddingRoomUserByEmail: builder.query({
            query: (email : string) => ({
                url: GET_BIDDING_ROOM_USER_BY_EMAIL,
                refetchOnMountArgChange: true,
                body: {
                    data: {
                        email : email
                    }
                },
                method : 'POST'
            }),
            // Update our currentUser client state everytime this action is called
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    const result = await queryFulfilled;

                    const userData = result.data.result.data;

                    const auth = getAuth();
                    const user = auth.currentUser;
                    const firebaseAuthToken = await user?.getIdToken();

                    const currentUser : User_BrUser = {
                        userId: userData.userId,
                        firstName: userData.firstName,
                        lastName: userData.lastName,
                        email: userData.email,
                        phoneNumber: userData.phoneNumber,
                        companyName: userData.companyName,
                        createdDate: userData.createdDate,
                        isActive: userData.isActive,
                        hasPortalAccess: userData.hasPortalAccess,
                        paymentsId: userData.paymentsId,
                        brUsersId: userData.brUsersId,
                        quotesLeft: userData.quotesLeft,
                        stripeCustomerId: userData.stripeCustomerId,
                        stripeSubscriptionId: userData.stripeSubscriptionId,
                        firebaseAuthToken: firebaseAuthToken,
                    }

                    dispatch(updateCurrentUser(currentUser)); 
                }
                catch (error) {
                    console.log(error);
                }
            },
        }),
        purchaseMaterialBid: builder.query({
            query: (request : PurchaseMaterialBidRequest) => ({
                url: PURCHASE_MATERIAL_BID,
                refetchOnMountArgChange: true,
                body: {
                    data: {
                        stripeCustomerId : request.stripeCustomerId,
                        userId : request.userId,
                        products : request.products,
                        deliveryAddress : request.deliveryAddress,
                    }
                },
                method : 'POST'
            }),
        }),
        getCustomer: builder.query({
            query: (stripeCustomerId : string) => ({
                url: GET_CUSTOMER,
                refetchOnMountArgChange: true,
                body: {
                    data: {
                        stripeCustomerId : stripeCustomerId,
                    }
                },
                method : 'POST'
            }),
        }),
        cancelPremiumSubscription: builder.query({
            query: (request : CancelPremiumSubscriptionRequest) => ({
                url: CANCEL_PREMIUM_SUBSCRIPTION,
                refetchOnMountArgChange: true,
                body: {
                    data: {
                        stripeCustomerId : request.stripeCustomerId,
                        comment : request.comment,
                        feedback : request.feedback
                    }
                },
                method : 'POST'
            }),
        }),        
        getAllBrOrders: builder.query({
            query: (test : string) => ({
                url: GET_ALL_BR_ORDERS,
                refetchOnMountArgChange: true,
                body: {
                    data: {
                        test : test,
                    }
                },
                method : 'POST'
            }),
        }),
        updateBrOrderStatusByOrderId: builder.query({
            query: (request : UpdateBrOrderStatusByOrderIdRequest) => ({
                url: UPDATE_BR_ORDER_STATUS_BY_ORDER_ID,
                refetchOnMountArgChange: true,
                body: {
                    data: {
                        orderId : request.orderId,
                        newOrderStatus : request.newOrderStatus
                    }
                },
                method : 'POST'
            }),
        }),
        getBrOrdersByUserId: builder.query({
            query: (userId : string) => ({
                url: GET_BR_ORDERS_BY_USER_ID,
                refetchOnMountArgChange: true,
                body: {
                    data: {
                        userId : userId,
                    }
                },
                method : 'POST'
            }),
        }),
        createPaymentMethodInStripeWithIxoToken: builder.query({
            query: (request : CreatePaymentMethodInStripeWithIxoTokenRequest) => ({
                url: import.meta.env.VITE_CREATE_PAYMENT_METHOD_IN_STRIPE_WITH_IXO_TOKEN,
                refetchOnMountArgChange: true,
                body: {
                    data: {
                        userId : request.userId,
                        email : request.email,
                        stripeCustomerId : request.stripeCustomerId,
                        billingAddress : request.billingAddress,
                        billingCity : request.billingCity,
                        billingState : request.billingState,
                        billingZip : request.billingZip,
                        cardHolderName : request.cardHolderName,
                        expirationDate : request.expirationDate,
                        ixoToken : request.ixoToken
                    }
                },
                method : 'POST'
            }),
        }),
        getTokenizeIframeConfig: builder.query({
            query: (origin : string) => ({
                url: import.meta.env.VITE_GET_TOKENIZE_IFRAME_CONFIG,
                refetchOnMountArgChange: true,
                body: {
                    data: {
                        origin : origin,
                    }
                },
                method : 'POST'
            }),
        }),
        getUpdateCVCIframeConfig: builder.query({
            query: (request : GetUpdateCVCIframeConfigRequest) => ({
                url: import.meta.env.VITE_GET_UPDATE_CVC_IFRAME_CONFIG,
                refetchOnMountArgChange: true,
                body: {
                    data: {
                        origin : request.origin,
                        userId : request.userId
                    }
                },
                method : 'POST'
            }),
        }),
        checkPaymentCardExists: builder.query({
            query: (userId : string) => ({
                url: import.meta.env.VITE_CHECK_PAYMENT_CARD_EXISTS,
                refetchOnMountArgChange: true,
                body: {
                    data: {
                        userId : userId,
                    }
                },
                method : 'POST'
            }),
        }),
        getPaymentCardByUserId: builder.query({
            query: (userId : string) => ({
                url: import.meta.env.VITE_GET_PAYMENT_CARD_BY_USER_ID,
                refetchOnMountArgChange: true,
                body: {
                    data: {
                        userId : userId,
                    }
                },
                method : 'POST'
            }),
        }),
        purchasePremiumMembership: builder.query({
            query: (request : PurchasePremiumSubscriptionRequest) => ({
                url: import.meta.env.VITE_PURCHASE_PREMIUM_MEMBERSHIP,
                refetchOnMountArgChange: true,
                body: {
                    data: {
                        userId : request.userId,
                        stripeCustomerId : request.stripeCustomerId
                    }
                },
                method : 'POST'
            }),
        }),
        checkIfUserHasPremiumMembership: builder.query({
            query: (stripeCustomerId : string) => ({
                url: import.meta.env.VITE_CHECK_IF_USER_HAS_PREMIUM_MEMBERSHIP,
                refetchOnMountArgChange: true,
                body: {
                    data: {
                        stripeCustomerId : stripeCustomerId
                    }
                },
                method : 'POST'
            }),
        }),
        detachDefaultPaymentMethodFromCustomer: builder.query({
            query: (request : DetachDefaultPaymentMethodFromCustomerRequest) => ({
                url: import.meta.env.VITE_DETACH_DEFAULT_PAYMENT_METHOD_FROM_CUSTOMER,
                refetchOnMountArgChange: true,
                body: {
                    data: {
                        userId : request.userId,
                        stripeCustomerId : request.stripeCustomerId
                    }
                },
                method : 'POST'
            }),
        }),
    })
})

export const {
    useLazySendSignupEmailQuery,
    useLazySerpApiHomeDepotSearchQuery,
    useLazySerpApiHomeDepotGetProductQuery,
    useLazySendOrderEmailQuery,
    useLazyCreateCustomerQuery,
    useLazyValidateBetaKeyQuery,
    useLazyCreateBiddingRoomUserQuery,
    useLazyGetBiddingRoomUserByEmailQuery,
    useLazyPurchaseMaterialBidQuery,
    useLazyGetCustomerQuery,
    useLazyCancelPremiumSubscriptionQuery,
    useLazyValidateOrderAdminKeyQuery,
    useLazyGetAllBrOrdersQuery,
    useLazyUpdateBrOrderStatusByOrderIdQuery,
    useLazyGetBrOrdersByUserIdQuery,
    useLazyCreatePaymentMethodInStripeWithIxoTokenQuery,
    useLazyGetTokenizeIframeConfigQuery,
    useLazyCheckPaymentCardExistsQuery,
    useLazyGetPaymentCardByUserIdQuery,
    useLazyGetUpdateCVCIframeConfigQuery,
    useLazyPurchasePremiumMembershipQuery,
    useLazyCheckIfUserHasPremiumMembershipQuery,
    useLazyDetachDefaultPaymentMethodFromCustomerQuery
} = firebaseFunctionsApi;
